import { forwardRef, useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  author?: string;
  imageAlt?: string;
  price?: string;
  currency?: string;
  availability?: 'in stock' | 'out of stock' | 'preorder';
  additionalStructuredData?: Record<string, any>;
}

export const SEO = forwardRef<unknown, SEOProps>(function SEO({
  title,
  description,
  keywords = 'photography, portrait, landscape, wedding, boudoir, branding, dark moody photography',
  image = '/og-image.jpg',
  url,
  type = 'website',
  publishedTime,
  author = 'Raven Moon Photography',
  imageAlt,
  price,
  currency = 'USD',
  availability,
  additionalStructuredData,
}: SEOProps, _ref) {
  // Keep the rendered title under Google's ~60-char snippet limit and never
  // repeat the brand name when the page title already contains it.
  const SITE_NAME = 'Raven Moon Photography';
  const baseTitle = title.replace(new RegExp(`\\s*[|\\-–]?\\s*${SITE_NAME}\\s*$`, 'i'), '').trim() || SITE_NAME;
  const withBrand = `${baseTitle} | ${SITE_NAME}`;
  const fullTitle =
    withBrand.length <= 60
      ? withBrand
      : baseTitle.length <= 60
        ? baseTitle
        : `${baseTitle.slice(0, 57).trimEnd()}…`;
  const siteUrl = window.location.origin;
  const fullUrl = url ? `${siteUrl}${url}` : window.location.href;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set meta tag
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('author', author);

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:image', fullImage, true);
    setMeta('og:image:alt', imageAlt || fullTitle, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', 'Raven Moon Photography', true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', fullImage);
    setMeta('twitter:image:alt', imageAlt || fullTitle);

    // Product specific Open Graph
    if (type === 'product') {
      if (price) {
        setMeta('product:price:amount', price, true);
        setMeta('product:price:currency', currency, true);
        setMeta('og:price:amount', price, true);
        setMeta('og:price:currency', currency, true);
      }
      if (availability) setMeta('product:availability', availability, true);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    // Article specific
    if (type === 'article' && publishedTime) {
      setMeta('article:published_time', publishedTime, true);
      setMeta('article:author', author, true);
    }

    // JSON-LD Structured Data
    const existingScript = document.querySelector('script[data-seo="structured-data"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = type === 'article'
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          name: 'Raven Moon Photography',
          description,
          url: fullUrl,
          image: fullImage,
          headline: title,
          datePublished: publishedTime,
          author: {
            '@type': 'Person',
            name: author,
          },
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#business`,
          name: 'Raven Moon Photography',
          description: 'Professional photography studio specializing in family portraits, brand photography, weddings, boudoir, and nature photography. Based in Wayne, Oklahoma, serving the entire state with worldwide travel availability.',
          url: siteUrl,
          image: fullImage,
          telephone: '+1-480-576-6053',
          email: 'ravenmoonphotography0@gmail.com',
          priceRange: '$$',
          currenciesAccepted: 'USD',
          paymentAccepted: 'Cash, Credit Card, Venmo, Zelle, PayPal',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Wayne',
            addressRegion: 'OK',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 34.9137,
            longitude: -97.3231,
          },
          areaServed: [
            {
              '@type': 'City',
              name: 'Wayne',
            },
            {
              '@type': 'City',
              name: 'Oklahoma City',
              '@id': 'https://www.wikidata.org/wiki/Q34863',
            },
            {
              '@type': 'State',
              name: 'Oklahoma',
            },
            {
              '@type': 'Country',
              name: 'United States',
            },
            {
              '@type': 'Text',
              name: 'Worldwide',
            },
          ],
          knowsAbout: [
            'Portrait Photography',
            'Wedding Photography',
            'Boudoir Photography',
            'Brand Photography',
            'Nature Photography',
            'Fine Art Prints',
          ],
          sameAs: [
            'https://www.instagram.com/raven__moon__photography',
            'https://www.facebook.com/share/188TB5SuQ6/',
            'https://www.tiktok.com/@raven_moon_photography',
          ],
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '09:00',
            closes: '18:00',
          },
          founder: {
            '@type': 'Person',
            name: 'Tiffany Laws',
            jobTitle: 'Photographer & Owner',
          },
        };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'structured-data');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Add additional structured data (e.g., FAQPage, BreadcrumbList)
    if (additionalStructuredData) {
      const existingAdditional = document.querySelector('script[data-seo="additional-structured-data"]');
      if (existingAdditional) existingAdditional.remove();
      
      const additionalScript = document.createElement('script');
      additionalScript.type = 'application/ld+json';
      additionalScript.setAttribute('data-seo', 'additional-structured-data');
      additionalScript.textContent = JSON.stringify(additionalStructuredData);
      document.head.appendChild(additionalScript);
    }

    return () => {
      const scriptToRemove = document.querySelector('script[data-seo="structured-data"]');
      if (scriptToRemove) scriptToRemove.remove();
      const additionalToRemove = document.querySelector('script[data-seo="additional-structured-data"]');
      if (additionalToRemove) additionalToRemove.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, keywords, fullImage, fullUrl, type, publishedTime, author, imageAlt, price, currency, availability, JSON.stringify(additionalStructuredData ?? null)]);

  return null;
});

SEO.displayName = 'SEO';
